import { motion } from "framer-motion";
import {
  MapPin, Building2, FileCheck2, Zap, ArrowRight, ShieldCheck, Sparkles, BadgeCheck,
  Mail, PhoneCall, Users, CheckCircle2, ClipboardList, Rocket, Check, X, Menu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import InquiryForm, { CITY_OPTIONS } from "@/components/InquiryForm";
import WaitlistForm from "@/components/WaitlistForm";
import CallbackDialog from "@/components/CallbackDialog";
import OfferPopup from "@/components/OfferPopup";
import Seo from "@/components/Seo";
import WhatsAppButton, { MobileQuickBar, WHATSAPP_LINK, WhatsAppIcon, whatsAppLink } from "@/components/WhatsAppButton";
import { CountdownInline } from "@/components/CountdownTimer";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1702047149248-a6049168d2a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwY293b3JraW5nJTIwc3BhY2UlMjBwZW9wbGUlMjBjb2xsYWJvcmF0aW5nfGVufDB8fHx8MTc5MzYxNzY3N3ww&ixlib=rb-4.1.0&q=85&w=1600&auto=format&fit=crop",
  office2: "https://images.unsplash.com/photo-1553028826-f4804a6dba3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwY293b3JraW5nJTIwc3BhY2UlMjBwZW9wbGUlMjBjb2xsYWJvcmF0aW5nfGVufDB8fHx8MTc5MzYxNzY3N3ww&ixlib=rb-4.1.0&q=85&w=1200&auto=format&fit=crop",
  comingSoon: "https://images.unsplash.com/photo-1631248055158-edec7a3c072b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBlbXB0eSUyMG9mZmljZSUyMGludGVyaW9yJTIwc3Vubnl8ZW58MHx8fHwxNzkzNjE3Njc3fDA&ixlib=rb-4.1.0&q=85&w=1920&auto=format&fit=crop",
  meeting: "https://images.unsplash.com/photo-1604328703693-18313fe20f3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwY293b3JraW5nJTIwc3BhY2UlMjBwZW9wbGUlMjBjb2xsYWJvcmF0aW5nfGVufDB8fHx8MTc5MzYxNzY3N3ww&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
  workspace: "https://images.unsplash.com/photo-1631248055158-edec7a3c072b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBlbXB0eSUyMG9mZmljZSUyMGludGVyaW9yJTIwc3Vubnl8ZW58MHx8fHwxNzkzNjE3Njc3fDA&ixlib=rb-4.1.0&q=85&w=1000&auto=format&fit=crop",
};

const HERO_VIDEO = "https://videos.pexels.com/video-files/7253092/7253092-hd_1920_1080_25fps.mp4";

const GALLERY = [
  { caption: "Collaborative coworking lounges", img: "https://images.unsplash.com/photo-1702047149248-a6049168d2a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwY293b3JraW5nJTIwc3BhY2UlMjBwZW9wbGUlMjBjb2xsYWJvcmF0aW5nfGVufDB8fHx8MTc5MzYxNzY3N3ww&ixlib=rb-4.1.0&q=85&w=900&auto=format&fit=crop" },
  { caption: "Premium meeting rooms", img: "https://images.unsplash.com/photo-1553028826-f4804a6dba3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwY293b3JraW5nJTIwc3BhY2UlMjBwZW9wbGUlMjBjb2xsYWJvcmF0aW5nfGVufDB8fHx8MTc5MzYxNzY3N3ww&ixlib=rb-4.1.0&q=85&w=900&auto=format&fit=crop" },
  { caption: "Focus-friendly work desks", img: "https://images.unsplash.com/photo-1604328703693-18313fe20f3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwY293b3JraW5nJTIwc3BhY2UlMjBwZW9wbGUlMjBjb2xsYWJvcmF0aW5nfGVufDB8fHx8MTc5MzYxNzY3N3ww&ixlib=rb-4.1.0&q=85&w=900&auto=format&fit=crop" },
  { caption: "Sunlit modern interiors", img: "https://images.unsplash.com/photo-1631248055158-edec7a3c072b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBlbXB0eSUyMG9mZmljZSUyMGludGVyaW9yJTIwc3Vubnl8ZW58MHx8fHwxNzkzNjE3Njc3fDA&ixlib=rb-4.1.0&q=85&w=900&auto=format&fit=crop" },
];

const CITIES = [
  { name: "Kolkata", img: "https://images.unsplash.com/photo-1679249010086-b8a932c8cafc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBjaXR5JTIwc2t5bGluZSUyMHN1bnJpc2V8ZW58MHx8fHwxNzkzNjE3Njc3fDA&ixlib=rb-4.1.0&q=85&w=800&auto=format&fit=crop" },
  { name: "Delhi", img: "https://images.unsplash.com/photo-1706545604042-399792bd8a04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBjaXR5JTIwc2t5bGluZSUyMHN1bnJpc2V8ZW58MHx8fHwxNzkzNjE3Njc3fDA&ixlib=rb-4.1.0&q=85&w=800&auto=format&fit=crop" },
  { name: "Bangalore", img: "https://images.unsplash.com/photo-1770353985048-c4d04d779667?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBjaXR5JTIwc2t5bGluZSUyMHN1bnJpc2V8ZW58MHx8fHwxNzkzNjE3Njc3fDA&ixlib=rb-4.1.0&q=85&w=800&auto=format&fit=crop" },
  { name: "Pune", img: "https://images.unsplash.com/photo-1793027516486-e2ccb87ebff5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBjaXR5JTIwc2t5bGluZSUyMHN1bnJpc2V8ZW58MHx8fHwxNzkzNjE3Njc3fDA&ixlib=rb-4.1.0&q=85&w=800&auto=format&fit=crop" },
  { name: "Hyderabad", img: "https://images.unsplash.com/photo-1604328703693-18313fe20f3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwY293b3JraW5nJTIwc3BhY2UlMjBwZW9wbGUlMjBjb2xsYWJvcmF0aW5nfGVufDB8fHx8MTc5MzYxNzY3N3ww&ixlib=rb-4.1.0&q=85&w=800&auto=format&fit=crop" },
];

const SERVICES = [
  { icon: Building2, title: "Virtual Office Address", desc: "A premium business address in a prime commercial hub — use it on your website, invoices, visiting cards and GST certificate." },
  { icon: FileCheck2, title: "GST Registration", desc: "GST-approved address with legal agreement and complete documentation support for fast, rejection-free registration." },
  { icon: BadgeCheck, title: "Company Registration", desc: "Incorporate your Private Limited, LLP or partnership at your virtual office address — handled end-to-end via Solvelegalx.com." },
  { icon: Mail, title: "Mail Handling", desc: "All your official correspondence received, scanned and forwarded to you — never miss an important notice." },
  { icon: PhoneCall, title: "Call Answering", desc: "Professional call answering and receptionist support so every client call is answered in your company's name." },
  { icon: Users, title: "Meeting Room Access", desc: "Need to meet a client in person? Book professional meeting rooms on a pay-per-use basis." },
];

const AUDIENCES = [
  { title: "Startups & New Businesses", desc: "Launch with a credible metro address from day one." },
  { title: "Freelancers & Consultants", desc: "Look established to clients without renting an office." },
  { title: "E-commerce Sellers", desc: "Get GST registration in new states to expand your market." },
  { title: "SMEs & Growing Companies", desc: "Enter new cities without the cost of physical branches." },
  { title: "Foreign Companies", desc: "Establish an Indian presence with a compliant registered address." },
  { title: "LLPs & Partnership Firms", desc: "Meet MCA and statutory address requirements easily." },
];

const DOCUMENTS = [
  "PAN Card of the business owner or authorised signatory",
  "Aadhaar Card or any valid government-issued ID proof",
  "Passport-size photograph of the owner or director",
  "Certificate of Incorporation or Partnership Deed (for registered entities)",
  "Business name, entity type and proposed use of the address",
];

const STEPS = [
  { title: "Share your requirement", desc: "Fill the inquiry form with your city, business type and duration." },
  { title: "We prepare your documents", desc: "Rent agreement, NOC and utility bill — the complete GST-ready set." },
  { title: "Register & get started", desc: "Use your new address for GST, MCA and banking — within a few days." },
];

const PACKAGES = [
  {
    id: "address",
    name: "Address Plan",
    price: 999,
    tagline: "For businesses that just need a premium address",
    features: [
      "Prime location address in your chosen metro city",
      "Mail handling & forwarding",
      "Address usable on website, invoices & cards",
    ],
    popular: false,
  },
  {
    id: "gst",
    name: "GST Plan",
    price: 1499,
    tagline: "Everything needed for GST registration",
    features: [
      "Prime location address in your chosen metro city",
      "GST-ready document set: rent agreement, NOC, utility bill",
      "GST application support via Solvelegalx.com",
      "FREE GST registration — festive offer, till Diwali",
      "Mail handling & forwarding",
    ],
    popular: true,
  },
  {
    id: "business",
    name: "Business Plan",
    price: 2499,
    tagline: "Complete setup for new companies",
    features: [
      "Everything in GST Plan",
      "Professional call answering",
      "Meeting room access on demand",
      "Priority support & dedicated account manager",
    ],
    popular: false,
  },
];

const ADDONS = [
  { id: "gst_filing_1yr", name: "1-Year GST Filing", price: 8999, unit: "one-time", desc: "A full year of GST return filing handled for you — works with any plan.", best: true },
  { id: "gst_filing_3m", name: "3-Month GST Filing", price: 2500, unit: "one-time", desc: "We file your GST returns for 3 months — stay compliant from day one." },
  { id: "signage", name: "Business Signage", price: 5000, unit: "one-time", desc: "Your company name displayed as signage at your virtual office location.", note: "with Business Plan" },
  { id: "company_reg", name: "Company Registration", price: 10000, unit: "one-time", desc: "Pvt Ltd / LLP registration handled end-to-end via Solvelegalx.com.", note: "with Business Plan" },
];

const FAQS = [
  { q: "Is a virtual office legal in India?", a: "Yes, completely. A virtual office address is legally accepted for GST registration, MCA company registration and as your official business correspondence address, as long as proper documentation (rent agreement, NOC, utility bill) is in place — which we provide." },
  { q: "Can I use a virtual office for GST registration?", a: "Yes. GST law requires a principal place of business, and a virtual office address fulfils this requirement. We provide the complete document set needed for a smooth, rejection-free GST application." },
  { q: "What documents do I need to get started?", a: "Just your PAN card, Aadhaar (or any government ID), a photograph, and your business details. For registered companies, the Certificate of Incorporation or Partnership Deed as well. We handle everything else." },
  { q: "How fast can I get my virtual office?", a: "Once you share your documents, we typically prepare your complete GST-ready document set within a few working days — much faster than finding and leasing a physical office." },
  { q: "Do you also handle company registration?", a: "Yes. Through Solvelegalx.com, we handle company incorporation, GST filing and ongoing legal compliance — so your business setup is managed end-to-end in one place." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export function Logo() {
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-logo">
      <img src="/solvedesk-icon.png" alt="SolveDesk logo" className="w-9 h-9 object-contain" />
      <span className="font-display font-bold text-xl tracking-tight text-slate-900">
        Solve<span className="text-primary">Desk</span>
      </span>
    </div>
  );
}

export default function LandingPage() {
  const [presetMessage, setPresetMessage] = useState("");
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const NAV_LINKS = [
    ["#services", "Services", "nav-services-link"],
    ["#pricing", "Pricing", "nav-pricing-link"],
    ["#cities", "Cities", "nav-cities-link"],
    ["#how-it-works", "How It Works", "nav-how-link"],
    ["#faq", "FAQs", "nav-faq-link"],
    ["/blog", "Blog", "nav-blog-link"],
  ];

  useEffect(() => {
    document.title = "SolveDesk — Virtual Offices Across India";
  }, []);
  const scrollToForm = () => document.getElementById("inquire")?.scrollIntoView({ behavior: "smooth" });

  const choosePlan = (plan) => {
    navigate(`/checkout?plan=${plan.id}`);
  };

  const chooseAddon = (addon) => {
    navigate(`/checkout?addons=${addon.id}`);
  };

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0" data-testid="landing-page">
      <Seo
        title="SolveDesk — Virtual Offices Across India"
        description="Premium virtual office addresses in Kolkata, Delhi, Bangalore, Pune, Hyderabad and all major Indian cities. GST and company registration support included."
        path="/"
      />
      <OfferPopup />
      <WhatsAppButton />
      <MobileQuickBar />

      {/* Festive offer banner */}
      {!bannerDismissed && (
        <div className="fixed top-0 inset-x-0 z-[60] bg-slate-900 text-white" data-testid="offer-banner">
          <div className="max-w-7xl mx-auto px-6 h-10 flex items-center justify-center gap-2 sm:gap-3">
            <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />
            <p className="text-[11px] sm:text-sm font-medium truncate">
              Festive Offer: 12 months for the price of 11 + FREE GST registration
            </p>
            <CountdownInline className="hidden sm:inline text-primary text-xs shrink-0" />
            <button onClick={() => window.dispatchEvent(new Event("soldesk:open-offer"))} data-testid="offer-banner-claim"
              className="shrink-0 rounded-full bg-primary hover:bg-primary/90 text-white text-xs font-bold px-3.5 py-1 transition-colors duration-200">
              Claim
            </button>
            <button onClick={() => setBannerDismissed(true)} data-testid="offer-banner-close" aria-label="Dismiss offer banner"
              className="shrink-0 text-white/60 hover:text-white transition-colors duration-200 ml-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {/* Header */}
      <header className={`fixed inset-x-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5 ${bannerDismissed ? "top-0" : "top-10"}`}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7 text-sm font-medium text-slate-600">
            {NAV_LINKS.map(([href, label, testId]) => (
              <a key={href} href={href} className="hover:text-slate-900 transition-colors duration-200" data-testid={testId}>{label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <CallbackDialog />
            <Button onClick={scrollToForm} data-testid="nav-inquire-button"
              className="rounded-full bg-slate-900 hover:bg-slate-800 text-white px-6 h-10 text-sm font-medium transition-transform duration-200 hover:-translate-y-0.5">
              Inquire Now
            </Button>
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-button" aria-label="Open menu"
                  className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors duration-200">
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0" data-testid="mobile-menu">
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
                  <SheetTitle className="text-left"><Logo /></SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col px-6 py-4">
                  {NAV_LINKS.map(([href, label, testId]) => (
                    <a key={href} href={href} onClick={() => setMenuOpen(false)} data-testid={`mobile-${testId}`}
                      className="py-3.5 text-base font-medium text-slate-700 hover:text-primary border-b border-slate-50 transition-colors duration-200">
                      {label}
                    </a>
                  ))}
                </nav>
                <div className="px-6 pt-2 space-y-3">
                  <Button onClick={() => { setMenuOpen(false); scrollToForm(); }} data-testid="mobile-menu-inquire-button"
                    className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold">
                    Inquire Now
                  </Button>
                  <CallbackDialog>
                    <Button variant="outline" data-testid="mobile-menu-callback-button"
                      className="w-full h-12 rounded-full border-slate-300 font-semibold">
                      <PhoneCall className="w-4 h-4 mr-2" /> Request Callback
                    </Button>
                  </CallbackDialog>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-24 px-6 scroll-mt-28" id="inquire">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <div className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-xs font-bold tracking-[0.15em] uppercase text-accent-foreground mb-8" data-testid="hero-overline">
              <Sparkles className="w-3.5 h-3.5" /> Virtual Offices Across India
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-bold text-slate-900 leading-[1.02]" data-testid="hero-headline">
              Premium Business Address.{" "}
              <span className="text-primary">Zero Office Costs.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg" data-testid="hero-subheadline">
              Establish your presence in India's biggest metro cities. Virtual office, GST and company
              registration — all in one place, with GST-ready documents in just a few days.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4" data-testid="hero-trust-points">
              {[
                { icon: MapPin, text: "Kolkata · Delhi · Bangalore · Pune · Hyderabad" },
                { icon: ShieldCheck, text: "Backed by Solvelegalx.com" },
                { icon: Zap, text: "Setup in days, not months" },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2.5 text-sm font-medium text-slate-700">
                  <span className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                    <Icon className="w-4 h-4 text-primary" />
                  </span>
                  {text}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
            <div className="relative">
              <div className="absolute -inset-1.5 rounded-[2rem] bg-primary/10 blur-2xl" aria-hidden />
              <div className="relative rounded-3xl border border-black/5 bg-white/80 backdrop-blur-xl shadow-[0_20px_40px_rgb(0,0,0,0.08)] p-8" data-testid="inquiry-form-card">
                <h2 className="font-display text-2xl font-semibold text-slate-900">Start your inquiry</h2>
                <p className="text-sm text-muted-foreground mt-1 mb-6">Tell us where you need your virtual office — we'll take it from there.</p>
                <InquiryForm source="hero" presetMessage={presetMessage} />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hero video strip */}
      <section className="px-6 pb-24">
        <motion.div initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-w-7xl mx-auto overflow-hidden rounded-[2rem] shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
          <video autoPlay muted loop playsInline preload="metadata" poster={IMAGES.hero}
            className="w-full h-72 sm:h-[520px] object-cover" data-testid="hero-video">
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent pointer-events-none" />
          <div className="absolute bottom-5 left-5 right-5 sm:bottom-8 sm:left-8 sm:right-8 flex flex-wrap items-end justify-between gap-4 pointer-events-none">
            <div>
              <p className="text-white/70 text-[10px] sm:text-xs font-bold tracking-[0.15em] sm:tracking-[0.2em] uppercase mb-1.5 sm:mb-2">Work from anywhere. Belong everywhere.</p>
              <p className="font-display text-lg sm:text-3xl font-semibold text-white" style={{ textShadow: "0 2px 12px rgb(0,0,0,0.4)" }}>
                Your business, at India's best addresses.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Services */}
      <section className="py-24 px-6 bg-secondary/50 scroll-mt-28" id="services">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">What We Offer</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900 max-w-2xl" data-testid="services-headline">
              Everything your business needs to go official.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-xl">
              From your registered address to GST filing and company incorporation — one partner for your entire business setup.
            </p>
          </motion.div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
            {SERVICES.map(({ icon: Icon, title, desc }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.06 }}
                className="rounded-3xl bg-white border border-black/5 p-8 shadow-[0_2px_8px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
                data-testid={`service-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-900">{title}</h3>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-6 scroll-mt-28" id="pricing">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="text-center">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">Simple Pricing</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900" data-testid="pricing-headline">
              Pick a plan that fits your business.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-xl mx-auto">
              Transparent monthly pricing. No lock-ins, no hidden charges — upgrade or cancel anytime.
            </p>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-testid="pricing-grid">
            {PACKAGES.map((plan, i) => (
              <motion.div key={plan.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`relative rounded-3xl p-8 flex flex-col ${
                  plan.popular
                    ? "bg-slate-900 text-white shadow-[0_20px_40px_rgb(0,0,0,0.15)] md:-translate-y-3"
                    : "bg-white border border-slate-200 shadow-[0_2px_8px_rgb(0,0,0,0.04)]"
                }`}
                data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}>
                {plan.popular && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-primary text-white text-xs font-bold px-4 py-1.5 tracking-wide uppercase" data-testid="pricing-popular-badge">
                    Most Popular
                  </span>
                )}
                <h3 className={`font-display text-xl font-semibold ${plan.popular ? "text-white" : "text-slate-900"}`}>{plan.name}</h3>
                <p className={`mt-1 text-sm ${plan.popular ? "text-white/60" : "text-muted-foreground"}`}>{plan.tagline}</p>
                <div className="mt-6 flex items-end gap-1.5">
                  <span className={`font-display text-5xl font-bold tracking-tight ${plan.popular ? "text-white" : "text-slate-900"}`}>
                    ₹{plan.price.toLocaleString("en-IN")}
                  </span>
                  <span className={`pb-1.5 text-sm ${plan.popular ? "text-white/60" : "text-muted-foreground"}`}>/month</span>
                </div>
                <p className={`mt-1 text-xs ${plan.popular ? "text-white/50" : "text-muted-foreground"}`}>
                  billed annually — ₹{(plan.price * 12).toLocaleString("en-IN")}/year
                </p>
                <ul className="mt-7 space-y-3.5 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                      <span className={`text-sm leading-relaxed ${plan.popular ? "text-white/85" : "text-slate-600"}`}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={() => choosePlan(plan)} data-testid={`pricing-choose-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`mt-8 w-full h-12 rounded-full font-semibold transition-transform duration-200 hover:-translate-y-0.5 ${
                    plan.popular
                      ? "bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/25"
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}>
                  Choose {plan.name}
                </Button>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-muted-foreground" data-testid="pricing-note">
            All plans available in Kolkata, Delhi, Bangalore, Pune, Hyderabad and other major cities. GST extra as applicable.
          </p>

          {/* Add-ons */}
          <div className="mt-12 max-w-5xl mx-auto" data-testid="addons-section">
            <p className="text-center text-xs font-bold tracking-[0.2em] uppercase text-primary mb-6">Power up your plan</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {ADDONS.map((addon) => (
                <motion.div key={addon.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5 }}
                  className="rounded-2xl border-2 border-dashed border-primary/30 bg-accent/40 p-6 flex items-start justify-between gap-4"
                  data-testid={`addon-card-${addon.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-lg font-semibold text-slate-900">{addon.name}</h3>
                      {addon.best && <span className="rounded-full bg-primary text-white text-xs font-bold px-2.5 py-0.5" data-testid="addon-best-badge">Best Value</span>}
                      {addon.note && <span className="rounded-full bg-primary/10 text-primary text-xs font-bold px-2.5 py-0.5">{addon.note}</span>}
                    </div>
                    <p className="mt-1.5 text-sm text-slate-600">{addon.desc}</p>
                    <p className="mt-3 font-display text-2xl font-bold text-slate-900">
                      +₹{addon.price.toLocaleString("en-IN")} <span className="text-sm font-medium text-muted-foreground">{addon.unit}</span>
                    </p>
                  </div>
                  <Button onClick={() => chooseAddon(addon)} variant="outline" data-testid={`addon-choose-${addon.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="rounded-full border-primary text-primary hover:bg-primary hover:text-white shrink-0 transition-colors duration-200">
                    Add
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="py-24 px-6 scroll-mt-28" id="cities">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">Pan-India Presence</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900 max-w-2xl" data-testid="cities-headline">
              One partner. Every major city.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-xl">
              Get a prime, GST-ready business address in the city your customers trust — without signing an expensive lease.
            </p>
          </motion.div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5" data-testid="cities-grid">
            {CITIES.map((city, i) => (
              <motion.div key={city.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group relative overflow-hidden rounded-2xl h-56 shadow-[0_8px_30px_rgb(0,0,0,0.06)] cursor-pointer"
                data-testid={`city-card-${city.name.toLowerCase()}`}>
                <img src={city.img} alt={`${city.name} business district`} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-display font-semibold text-lg" style={{ textShadow: "0 1px 8px rgb(0,0,0,0.4)" }}>{city.name}</p>
                  <p className="text-white/80 text-xs font-medium">Virtual offices available</p>
                </div>
              </motion.div>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground" data-testid="cities-more">
            + Mumbai, Chennai and other major cities — tell us where you need to be.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 bg-secondary/50 scroll-mt-28" id="how-it-works">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">How It Works</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900 max-w-2xl" data-testid="how-headline">
              GST-ready in three simple steps.
            </h2>
          </motion.div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="steps-grid">
            {STEPS.map((step, i) => (
              <motion.div key={step.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative rounded-3xl bg-white border border-black/5 p-8 shadow-[0_2px_8px_rgb(0,0,0,0.04)]"
                data-testid={`step-card-${i + 1}`}>
                <span className="font-display text-6xl font-bold text-primary/15">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">{step.title}</h3>
                <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="mt-10 text-center">
            <Button onClick={scrollToForm} data-testid="how-cta-button"
              className="rounded-full bg-primary hover:bg-primary/90 text-white px-8 h-12 font-semibold shadow-lg shadow-primary/25 transition-transform duration-200 hover:-translate-y-0.5">
              Start Step 1 — It's Free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Documents required */}
      <section className="py-24 px-6 scroll-mt-28" id="documents">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
            className="overflow-hidden rounded-[2rem] shadow-[0_20px_40px_rgb(0,0,0,0.08)] order-2 lg:order-1">
            <img src={IMAGES.office2} alt="Modern office interior with professionals working" loading="lazy" className="w-full h-[420px] object-cover" data-testid="documents-image" />
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="order-1 lg:order-2">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">Simple Documentation</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900" data-testid="documents-headline">
              All you need is a handful of documents.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600">
              No property hunting, no landlord negotiations. Share these basics and we prepare the rest.
            </p>
            <ul className="mt-8 space-y-4" data-testid="documents-list">
              {DOCUMENTS.map((doc) => (
                <li key={doc} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm sm:text-base text-slate-700">{doc}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Who is it for */}
      <section className="py-24 px-6 bg-slate-900 scroll-mt-28" id="who">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">Who It's For</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-white max-w-2xl" data-testid="who-headline">
              Built for businesses that think big, start lean.
            </h2>
          </motion.div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="who-grid">
            {AUDIENCES.map(({ title, desc }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.06 }}
                className="rounded-3xl bg-white/5 border border-white/10 p-8 hover:bg-white/10 transition-colors duration-300"
                data-testid={`who-card-${i}`}>
                <h3 className="font-display text-lg font-semibold text-white">{title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 px-6 scroll-mt-28" id="gallery">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">Inside Our Spaces</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900 max-w-2xl" data-testid="gallery-headline">
              Addresses your clients will remember.
            </h2>
          </motion.div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="gallery-grid">
            {GALLERY.map((item, i) => (
              <motion.div key={item.caption} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group relative overflow-hidden rounded-2xl h-64 shadow-[0_8px_30px_rgb(0,0,0,0.06)]"
                data-testid={`gallery-item-${i}`}>
                <img src={item.img} alt={item.caption} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <p className="absolute bottom-4 left-4 right-4 text-white text-sm font-semibold" style={{ textShadow: "0 1px 8px rgb(0,0,0,0.4)" }}>
                  {item.caption}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="relative py-32 px-6 overflow-hidden scroll-mt-28" id="coming-soon">
        <img src={IMAGES.comingSoon} alt="Sunlit modern workspace interior" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}
          className="relative max-w-3xl mx-auto text-center" data-testid="coming-soon-section">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-white/80">Coming Soon</p>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-white" data-testid="coming-soon-headline">
            Affordable workspaces. Flexible memberships.
          </h2>
          <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-xl mx-auto">
            Physical coworking spaces with plans that flex around your team are brewing at Solve Desk.
            Join the waitlist and be the first to know when doors open.
          </p>
          <WaitlistForm />
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 scroll-mt-28" id="faq">
        <div className="max-w-3xl mx-auto">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="text-center">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">FAQs</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900" data-testid="faq-headline">
              Questions? Answered.
            </h2>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }}
            className="mt-12" data-testid="faq-accordion">
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`}
                  className="rounded-2xl border border-slate-100 bg-white px-6 shadow-[0_2px_8px_rgb(0,0,0,0.04)]"
                  data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base sm:text-lg font-semibold text-slate-900 hover:no-underline py-5">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-slate-600 leading-relaxed pb-5">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 bg-secondary/50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}>
            <Rocket className="w-10 h-10 text-primary mx-auto mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900" data-testid="final-cta-headline">
              Your business deserves a better address.
            </h2>
            <p className="mt-4 text-lg text-slate-600">One inquiry is all it takes. No pricing games — just a plan built for your business.</p>
            <Button onClick={scrollToForm} data-testid="final-cta-button"
              className="mt-8 rounded-full bg-primary hover:bg-primary/90 text-white px-10 h-14 text-base font-semibold shadow-lg shadow-primary/25 transition-transform duration-200 hover:-translate-y-0.5">
              Inquire Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <Logo />
          <p className="text-sm text-muted-foreground" data-testid="footer-tagline">
            A <a href="https://solvelegalx.com" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline">Solvelegalx.com</a> initiative
          </p>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link"
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366]/10 text-[#128C4A] hover:bg-[#25D366] hover:text-white px-5 py-2.5 text-sm font-semibold transition-colors duration-200">
            <WhatsAppIcon className="w-4 h-4" /> +91 62900 74471
          </a>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <a href="/blog" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200" data-testid="footer-blog-link">Blog</a>
            <a href="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200" data-testid="footer-terms-link">Terms</a>
            <a href="/refund-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200" data-testid="footer-refund-link">Refund Policy</a>
            <a href="/privacy-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200" data-testid="footer-privacy-link">Privacy</a>
            <a href="/admin" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200" data-testid="footer-admin-link">Admin</a>
            <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} solvedesk.space</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
